import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  MapPin,
  TrendingUp,
  Building2,
  Users,
  Shield,
  Settings,
  CheckCircle,
  ArrowRight,
  Globe,
  Banknote,
  Clock,
  FileText,
  GraduationCap,
  Cpu,
  Navigation,
  Briefcase,
} from "lucide-react";

const DoingBusinessPage = () => {
  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  const whyBiH = [
    {
      icon: MapPin,
      title: "Strategic Location",
      description: "Positioned at the crossroads of Central and Southeast Europe, providing access to regional markets.",
    },
    {
      icon: Banknote,
      title: "Competitive Costs",
      description: "Lower operational costs compared to EU markets while maintaining quality standards.",
    },
    {
      icon: Users,
      title: "Skilled Workforce",
      description: "Educated, multilingual workforce with strong technical and professional skills.",
    },
    {
      icon: Globe,
      title: "EU Candidate Status",
      description: "EU candidate country with ongoing alignment to European standards and regulations.",
    },
  ];

  const mostarAdvantages = [
    {
      icon: Globe,
      title: "EU Proximity",
      description: "Direct access to Croatia and the Adriatic region, providing seamless connectivity to EU markets.",
    },
    {
      icon: GraduationCap,
      title: "Skilled Workforce",
      description: "Access to talent from universities and technical institutions producing professionals in technology, engineering, finance and business.",
    },
    {
      icon: Banknote,
      title: "Lower Costs",
      description: "Competitive operational and administrative costs compared to larger capital cities.",
    },
    {
      icon: Cpu,
      title: "Tech Ecosystem",
      description: "Strong growth of technology and service industries with an emerging entrepreneurial ecosystem.",
    },
    {
      icon: Navigation,
      title: "Regional Operations",
      description: "Excellent environment for companies establishing regional operations across Western Balkans.",
    },
  ];

  const whyMostarOverCapitals = [
    "Lower operational costs than Sarajevo or other regional capitals",
    "Access to same quality talent pool with less competition for employees",
    "Proximity to EU border (Croatia) for easy business travel",
    "Better quality of life for expat employees",
    "Easier hiring and workforce retention",
    "Growing tech and service ecosystem with startup mentality",
  ];

  const taxInfo = [
    { label: "Corporate Income Tax", value: "10%", note: "One of the lowest in Europe" },
    { label: "VAT Standard Rate", value: "17%", note: "Below EU average" },
    { label: "Personal Income Tax", value: "10%", note: "Flat rate" },
    { label: "Social Contributions", value: "~31%", note: "Employer contributions" },
  ];

  const companyFormationSteps = [
    {
      step: "1",
      title: "Name Reservation",
      description: "Reserve your company name with the relevant registration court.",
      duration: "1-2 days",
    },
    {
      step: "2",
      title: "Documentation Preparation",
      description: "Prepare founding documents, articles of association and director appointments.",
      duration: "2-3 days",
    },
    {
      step: "3",
      title: "Court Registration",
      description: "Submit registration application to the competent court for company registration.",
      duration: "5-10 days",
    },
    {
      step: "4",
      title: "Tax Registration",
      description: "Register with tax authorities and obtain tax identification number.",
      duration: "3-5 days",
    },
    {
      step: "5",
      title: "Bank Account Opening",
      description: "Open corporate bank account with a local or international bank.",
      duration: "5-7 days",
    },
    {
      step: "6",
      title: "Operational Setup",
      description: "Complete VAT registration, social security registration and commence operations.",
      duration: "5-7 days",
    },
  ];

  const complianceRequirements = [
    "Annual financial statements filing",
    "Corporate tax returns",
    "VAT reporting (monthly or quarterly)",
    "Payroll tax and social contributions",
    "Statistical reporting requirements",
    "Beneficial ownership registration",
    "Transfer pricing documentation (if applicable)",
    "GDPR compliance for EU data processing",
  ];

  const operationalConsiderations = [
    {
      icon: Building2,
      title: "Office Establishment",
      description: "Options include leasing commercial space, co-working spaces, or virtual office arrangements in Mostar and other cities.",
    },
    {
      icon: Users,
      title: "Hiring Employees",
      description: "Employment contracts must comply with local labor law. Work permits required for non-BiH nationals.",
    },
    {
      icon: Shield,
      title: "Banking & Finance",
      description: "International and local banks operate in BiH. Multi-currency accounts available.",
    },
    {
      icon: Settings,
      title: "IT Infrastructure",
      description: "Good internet connectivity and IT infrastructure in Mostar and major cities.",
    },
  ];

  return (
    <div data-testid="doing-business-page" className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#0B2A4A] to-[#0B2A4A]/95 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#2F6FED]" />
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold">
                Investment Guide
              </p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] text-white mb-6 font-['Sora']">
              Doing Business in Bosnia and Herzegovina
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-300 mb-8">
              A comprehensive guide for international companies considering 
              establishing operations in Bosnia and Herzegovina.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={openCalendly}
                className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-6 py-3 rounded-md"
              >
                Schedule a Consultation
              </Button>
              <Link to="/services">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0B2A4A] font-medium px-6 py-3 rounded-md"
                >
                  View Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Bosnia Section */}
      <section id="why-bih" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Why Bosnia and Herzegovina
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
              A Growing Business Destination
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Bosnia and Herzegovina offers a strategic location, competitive operating 
              costs and a skilled workforce, making it an attractive destination for 
              international businesses looking to establish a presence in Southeast Europe.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyBiH.map((item, index) => (
              <Card key={index} className="bg-[#F5F7FA] border-0 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#2F6FED]/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-[#2F6FED]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0B2A4A] mb-2 font-['Sora']">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mostar as Operational Hub - DETAILED SECTION */}
      <section id="mostar" className="py-20 md:py-28 bg-[#0B2A4A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-[#2F6FED]" />
                <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold">
                  Operational Hub
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
                Mostar as an Operational Hub
              </h2>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  Mostar has emerged as an increasingly attractive operational hub for 
                  international companies establishing a presence in Bosnia and Herzegovina. 
                  Its geographic location near the European Union border provides logistical 
                  advantages for businesses working across European and regional markets.
                </p>
                <p>
                  The city has developed a strong talent pool supported by universities and 
                  technical institutions, producing professionals in technology, engineering, 
                  finance and business administration. Combined with competitive operating 
                  costs and a growing entrepreneurial ecosystem, Mostar offers an ideal 
                  environment for companies seeking efficiency and flexibility.
                </p>
                <p>
                  For many international organizations, establishing operational teams in 
                  Mostar provides a practical alternative to larger capital cities while 
                  maintaining strong connectivity to key business centers in the region.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {mostarAdvantages.map((item, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#2F6FED]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#2F6FED]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                      <p className="text-slate-400 text-sm">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Mostar Instead of Capitals */}
      <section className="py-20 md:py-28 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-[#2F6FED]" />
                <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold">
                  Strategic Choice
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
                Why Companies Choose Mostar Instead of Larger Capitals
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                For international companies seeking a reliable regional base, Mostar offers 
                distinct advantages over traditional choices like Sarajevo or other regional capitals.
              </p>
              <ul className="space-y-3">
                {whyMostarOverCapitals.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED] mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100">
              <h3 className="text-lg font-semibold text-[#0B2A4A] mb-6 font-['Sora']">
                VTZ Backoffice Headquarters
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
                  <div className="w-12 h-12 bg-[#2F6FED]/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#2F6FED]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0B2A4A]">Location</p>
                    <p className="text-sm text-slate-500">Mostar, Bosnia and Herzegovina</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
                  <div className="w-12 h-12 bg-[#2F6FED]/10 rounded-lg flex items-center justify-center">
                    <Globe className="w-6 h-6 text-[#2F6FED]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0B2A4A]">EU Distance</p>
                    <p className="text-sm text-slate-500">30 min to Croatian border</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#2F6FED]/10 rounded-lg flex items-center justify-center">
                    <Navigation className="w-6 h-6 text-[#2F6FED]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0B2A4A]">Regional Access</p>
                    <p className="text-sm text-slate-500">Western Balkans coverage</p>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <Button
                  onClick={openCalendly}
                  className="w-full bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium py-3 rounded-md"
                >
                  Schedule a Visit to Mostar
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Economic Overview */}
      <section id="economic" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
                Economic Overview
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
                Economic Environment
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Bosnia and Herzegovina has a developing market economy with key sectors 
                  including manufacturing, services, energy and agriculture. The country 
                  maintains a currency board arrangement with the Euro.
                </p>
                <p>
                  The country is a member of CEFTA (Central European Free Trade Agreement) 
                  and has trade agreements providing preferential access to regional markets.
                </p>
                <p>
                  Foreign direct investment is welcomed across sectors, with incentives 
                  available in certain free zones and for qualifying investments.
                </p>
              </div>
            </div>
            <div className="bg-[#F5F7FA] rounded-xl p-8">
              <h3 className="text-lg font-semibold text-[#0B2A4A] mb-6 font-['Sora']">
                Key Economic Indicators
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                  <span className="text-slate-600">Currency</span>
                  <span className="font-semibold text-[#0B2A4A]">BAM (Convertible Mark)</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                  <span className="text-slate-600">Exchange Rate</span>
                  <span className="font-semibold text-[#0B2A4A]">1 EUR = 1.95583 BAM (fixed)</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-slate-200">
                  <span className="text-slate-600">Trade Agreements</span>
                  <span className="font-semibold text-[#0B2A4A]">CEFTA, SAA with EU</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">EU Status</span>
                  <span className="font-semibold text-[#2F6FED]">Candidate Country</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tax Environment */}
      <section id="tax" className="py-20 md:py-28 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Tax Environment
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
              Competitive Tax Rates
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Bosnia and Herzegovina offers one of the most competitive tax environments 
              in Europe, with low corporate and personal income tax rates.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {taxInfo.map((tax, index) => (
              <Card key={index} className="bg-[#0B2A4A] border-0">
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-white mb-2 font-['Sora']">{tax.value}</p>
                  <p className="text-white font-medium mb-1">{tax.label}</p>
                  <p className="text-sm text-slate-400">{tax.note}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-12 bg-white rounded-xl p-8 shadow-sm">
            <h3 className="text-lg font-semibold text-[#0B2A4A] mb-4 font-['Sora']">
              Additional Tax Considerations
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-slate-600">
              <div>
                <h4 className="font-medium text-[#0B2A4A] mb-2">Double Tax Treaties</h4>
                <p className="text-sm">
                  Bosnia and Herzegovina has double tax treaties with numerous countries 
                  to prevent double taxation of cross-border income.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-[#0B2A4A] mb-2">Withholding Taxes</h4>
                <p className="text-sm">
                  Withholding taxes apply to certain payments to non-residents, subject 
                  to treaty relief where applicable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Formation Process */}
      <section id="formation" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Company Formation
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
              Company Formation Process
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              The company formation process in Bosnia and Herzegovina typically takes 
              3-4 weeks from initiation to operational readiness.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {companyFormationSteps.map((step, index) => (
              <Card key={index} className="bg-[#F5F7FA] border-0">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 bg-[#2F6FED] rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">{step.step}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Clock className="w-4 h-4" />
                      {step.duration}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-[#0B2A4A] mb-2 font-['Sora']">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/services/corporate">
              <Button className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-6 py-3 rounded-md">
                View Corporate Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hiring Employees */}
      <section id="hiring" className="py-20 md:py-28 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
                Hiring Employees
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
                Employment in Bosnia and Herzegovina
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Employment relationships in Bosnia and Herzegovina are governed by 
                  entity-level labor laws. Employment contracts must be in writing and 
                  comply with applicable labor regulations.
                </p>
                <p>
                  Foreign nationals require work permits to be employed in Bosnia and 
                  Herzegovina. The work permit process is coordinated through the 
                  relevant employment agency.
                </p>
                <p>
                  Employers are responsible for withholding and remitting payroll taxes 
                  and social contributions on behalf of employees.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/services/employment">
                  <Button className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-medium px-6 py-3 rounded-md">
                    Employment Advisory Services
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-lg font-semibold text-[#0B2A4A] mb-6 font-['Sora']">
                Key Employment Considerations
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2F6FED] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0B2A4A]">Written Contracts Required</p>
                    <p className="text-sm text-slate-600">All employment must be formalized in writing</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2F6FED] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0B2A4A]">Work Permits for Foreigners</p>
                    <p className="text-sm text-slate-600">Non-BiH nationals require work authorization</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2F6FED] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0B2A4A]">Social Contributions</p>
                    <p className="text-sm text-slate-600">Employer contributions approximately 10.5%</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2F6FED] mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0B2A4A]">Annual Leave</p>
                    <p className="text-sm text-slate-600">Minimum 20 working days per year</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Requirements */}
      <section id="compliance" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Compliance Requirements
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
              Regulatory Compliance
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Companies operating in Bosnia and Herzegovina must comply with various 
              reporting and regulatory requirements.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-[#F5F7FA] border-0">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-6 h-6 text-[#2F6FED]" />
                  <h3 className="text-lg font-semibold text-[#0B2A4A] font-['Sora']">
                    Ongoing Compliance Obligations
                  </h3>
                </div>
                <ul className="space-y-3">
                  {complianceRequirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-[#2F6FED] mt-1 flex-shrink-0" />
                      <span className="text-sm">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-[#F5F7FA] border-0">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="w-6 h-6 text-[#2F6FED]" />
                  <h3 className="text-lg font-semibold text-[#0B2A4A] font-['Sora']">
                    VTZ Compliance Support
                  </h3>
                </div>
                <p className="text-slate-600 mb-6">
                  VTZ Backoffice in Mostar provides comprehensive compliance support to ensure 
                  your business meets all regulatory requirements in Bosnia and Herzegovina.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#0B2A4A]">Compliance Monitoring</p>
                      <p className="text-sm text-slate-600">Ongoing tracking of obligations and deadlines</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#0B2A4A]">Regulatory Filings</p>
                      <p className="text-sm text-slate-600">Preparation and submission of required reports</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2F6FED] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#0B2A4A]">Advisory Services</p>
                      <p className="text-sm text-slate-600">Guidance on regulatory changes and requirements</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  <Link to="/services/compliance">
                    <Button className="w-full bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium py-3 rounded-md">
                      View Compliance Services
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Operational Setup */}
      <section id="operations" className="py-20 md:py-28 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold mb-4">
              Operational Setup
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0B2A4A] mb-6 font-['Sora']">
              Setting Up Operations
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Practical considerations for establishing and running business operations 
              in Bosnia and Herzegovina, including options in Mostar.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {operationalConsiderations.map((item, index) => (
              <Card key={index} className="bg-white border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2F6FED]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[#2F6FED]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#0B2A4A] mb-2 font-['Sora']">
                        {item.title}
                      </h3>
                      <p className="text-sm text-slate-600">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#0B2A4A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
            Ready to Enter Bosnia and Herzegovina?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            VTZ Backoffice in Mostar provides the corporate and operational infrastructure you 
            need to successfully establish your business in Bosnia and Herzegovina.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={openCalendly}
              className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-8 py-4 rounded-md transition-all duration-200 shadow-lg"
            >
              Schedule a Consultation
            </Button>
            <Link to="/services">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B2A4A] font-medium px-8 py-4 rounded-md transition-all duration-200"
              >
                View Our Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DoingBusinessPage;
